// Card 19, curved place names (R548, ui-map-interface.md §17.3.19).
//
// Hand lettering follows the coast it names, spaced along the curve, so a
// name belongs to a shape and not to a point. Here a ridge and a trench take
// a name from the identity pools when the world arrives, and chat and orders
// can then say "at Drifter Shoal" and "along the Sounding" and both readers
// mean the same water.
//
// **THE ARC IS ONE CIRCLE OF SLIGHT, UNIFORM CURVATURE** (R558). The name
// must not lie on the rim it names, so the traced outline is offset outward
// by `trench_offset` or `ridge_offset` (`render/offset-loop.ts`), and the
// run's apex is that outline's top-most point. The run then turns through
// `curve_deg` in all, on a circle whose centre lies straight below the
// apex, so the name is level and bulges upward over the crown of the body:
// it follows the blob of the body and not the hex steps of its border. The
// first build laid the run along the offset
// outline itself, and a hex-bound outline turns sixty degrees at a time,
// so a name bent round every step. The plane's distance channels cannot
// answer any of this: they are inside distances and read zero outside a
// body.
//
// **THE RUN IS CENTRED ON THE APEX** and it reads left to right: the arc
// is sampled from left to right, and `layoutAlongPath` turns each glyph to
// the tangent under it.
//
// **A NAMED TRENCH CARRIES A DEDICATION** (R1251, card 11 of round 045).
// A trench a Clan War champion named takes the granted name, and above it
// a second arc, further out by `dedication_gap_px`, says whose it is:
// "in honour of the champions of Clan War XII: {Clan}" in the historical
// reading, "CLAN WAR XII · {CLAN}" in the modern one. Both lines lie on
// the uniform arc of R558 and not on the rim-following run of the mockup.
// The modern reading adds the depth figure at the centroid, derived from
// the trench size (`depthFigure`). A named trench always takes one of the
// `max_names` slots.
//
// **A NAMED SEAMOUNT CARRIES ITS NAME AND ITS NAMER** (R2620, R2633). A
// seamount is a ridge body, and the ridge whose cells hold the label cell
// of a name of `GameState.seamounts` takes that name in place of its pool
// name, in the feature step of a trench name and in the ink of the rock,
// at every zoom. The line above it names the commander, on the arc of a
// dedication. A named seamount takes a slot after the named trenches. A
// changed name marks `seamounts`, so the piece packs on a name and never in
// a frame (§10).
//
// **A NAME IS A CHART TEXT STEP** (R1796, §17.4). A trench name takes the
// feature step, a ridge name the name step, and the dedication and the
// depth figure the note step. The screen band holds each word; the name
// and its dedication grow about ONE pivot, the apex of the name's arc, so
// the dedication keeps its gap over the name at every zoom.
//
// **THE RIDGE NAME WAITS FOR `ridge_zoom_min`.** That figure is 1.0, which
// is `bottom_nature`'s own zoom band edge, so a camera crossing it marks
// `zoomband` and both pieces pack again. The test below pins the pair.

import { CHART, TERRAIN_TRENCH_RIM } from '../../../gen/presentation';
import { offsetLoop } from '../../../render/offset-loop';
import type { Point } from '../../../render/contours';
import { polylineLength, runWidth } from '../../../render/text-run';
import type { Pt } from '../../../render/text-run';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { chart as S } from '../../../gen/strings';
import { toRoman } from '../../../render/roman';
import { CHART_INK, crownOf, faceOf, longestLoop } from './shared';
import { clamp01 } from '../../../render/math';
import type { NamedSeamount } from '../../../gen/proto/aquaphobia';
import { seamountOnBody } from '../../../seamounts';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.place_names;

/** One name, and the arc it is laid along. */
interface Name {
  text: string;
  pts: Pt[];
  /** The middle of the arc: the pivot the band scale grows the name about. */
  pivot: Pt;
  /** Where along the arc the run is centred. */
  startS: number;
  kind: 'ridge' | 'trench' | 'seamount';
  /** The dedication of a named trench (R1251) or of a seamount name
   *  (R2633), on its own arc. */
  dedication?: { text: string; pts: Pt[]; startS: number };
  /** The depth figure of a named trench, at its centroid (modern only). */
  depth?: { text: string; x: number; y: number };
}

/**
 * The depth figure of a named trench (R1251): it grows with the trench
 * size from `depth_min` at one cell to `depth_max` at `depth_size_full`
 * cells, with one decimal after a middle dot, as card 11 writes "9·5".
 */
export function depthFigure(size: number): string {
  const f = T.fig;
  const span = Math.max(1, f.depth_size_full - 1);
  const t = clamp01((size - 1) / span);
  const d = f.depth_min + (f.depth_max - f.depth_min) * t;
  return d.toFixed(1).replace('.', '·');
}

export class PlaceNamesPiece implements Piece {
  readonly id = 'place_names' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The water the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'seamounts'] as const;

  private names: Name[] = [];

  visible(ctx: ChartContext): boolean {
    return ctx.terrain !== null && ctx.names !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.names = [];
    const terrain = ctx.terrain;
    const named = ctx.names;
    if (!terrain || !named) return;
    // The ridge body of each seamount name (R2633), by the label cell of
    // the name. The list is empty while `discovery` is off.
    const mounts = new Map<number, NamedSeamount>();
    const list = ctx.state.seamounts.list;
    if (list.length > 0) {
      for (const f of terrain.ridges) {
        const n = seamountOnBody(f.cells, list);
        if (n) mounts.set(f.id, n);
      }
    }
    // A named trench always takes a slot (R1251), and a named seamount
    // takes one after them (R2633), so the named bodies go first and the
    // rest fill the slots by size.
    const sorted = [...terrain.ridges, ...terrain.trenches]
      .sort((a, b) => (b.size - a.size) || (a.id - b.id));
    const all = [
      ...sorted.filter((f) => named.grantOf(f.id)),
      ...sorted.filter((f) => mounts.has(f.id)),
      ...sorted.filter((f) => !named.grantOf(f.id) && !mounts.has(f.id)),
    ].slice(0, T.fig.max_names);
    const style = ctx.cfg.pieces.place_names[ctx.reading];
    const face = faceOf(ctx.reading);
    const modern = ctx.reading === 'modern';
    for (const f of all) {
      const mount = mounts.get(f.id);
      const text = mount ? mount.name : named.nameOf(f.id);
      if (!text) continue;
      const loop = longestLoop(f.outline);
      if (!loop || loop.length < 3) continue;
      const trench = f.kind === 'trench';
      // A seamount name takes the feature step of a trench name.
      const big = trench || mount !== undefined;
      const offset = trench ? T.fig.trench_offset : T.fig.ridge_offset;
      const rim = offsetLoop(loop, offset);
      // The run's length in world px, in the face and size it will be set
      // in, so the arc turns through `curve_deg` whatever the name's length.
      const m = ctx.glyphs.stepMetrics(face, big ? T.text.trench : T.text.ridge);
      const spacing = big ? style.fig.trench_spacing : style.fig.ridge_spacing;
      const len = runWidth(text, m, spacing);
      const arc = uniformArc(rim, len, T.fig.curve_deg, T.fig.arc_step_px);
      const name: Name = {
        text, pts: arc, pivot: arcMiddle(arc), startS: polylineLength(arc, false) / 2,
        kind: mount ? 'seamount' : f.kind,
      };
      if (mount) {
        // The line above the name says who named it, as the dedication of
        // a named trench says whose it is.
        const ded = modern
          ? S.seamount_dedication_modern({ commander: mount.commander })
          : S.seamount_dedication_historical({ commander: mount.commander });
        name.dedication = dedicationArc(ctx, loop, offset, ded);
      }
      const grant = trench ? named.grantOf(f.id) : undefined;
      if (grant) {
        const war = toRoman(grant.war);
        const ded = modern
          ? S.named_trench_dedication_modern({ war, clan: grant.clan })
          : S.named_trench_dedication_historical({ war, clan: grant.clan });
        name.dedication = dedicationArc(ctx, loop, offset, ded);
        if (style.fig.depth_alpha > 0) {
          name.depth = { text: depthFigure(f.size), x: f.centroid.x, y: f.centroid.y };
        }
      }
      this.names.push(name);
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const style = ctx.cfg.pieces.place_names[ctx.reading];
    const face = faceOf(ctx.reading);
    const ridgeOn = ctx.zoom >= T.fig.ridge_zoom_min;
    for (const n of this.names) {
      if (n.kind === 'ridge' && !ridgeOn) continue;
      const trench = n.kind === 'trench';
      // A seamount name (R2633) is set as a named trench is, in the ink of
      // the rock it names, at every zoom.
      const big = n.kind !== 'ridge';
      ctx.glyphs.wordAlong(
        sinks.quads, n.text, n.pts, face,
        big ? T.text.trench : T.text.ridge,
        trench ? TERRAIN_TRENCH_RIM.color : CHART_INK.ridge,
        big ? T.fig.trench_alpha : T.fig.ridge_alpha,
        {
          anchor: 'centre', startS: n.startS, closed: false, pivot: n.pivot,
          letterSpacing: big ? style.fig.trench_spacing : style.fig.ridge_spacing,
        },
      );
      if (n.dedication) {
        ctx.glyphs.wordAlong(
          sinks.quads, n.dedication.text, n.dedication.pts, face,
          T.text.dedication, CHART_INK.dim, style.fig.dedication_alpha,
          {
            anchor: 'centre', startS: n.dedication.startS, closed: false, pivot: n.pivot,
            letterSpacing: style.fig.dedication_spacing,
          },
        );
      }
      if (n.depth) {
        ctx.glyphs.word(
          sinks.quads, n.depth.text, n.depth.x, n.depth.y, face,
          T.text.depth, CHART_INK.pale, style.fig.depth_alpha, { anchor: 'centre' },
        );
      }
    }
  }

  /** The names the last rebuild drew. The test reads them. */
  get drawn(): readonly {
    text: string; kind: string; dedication?: { text: string }; depth?: { text: string };
  }[] { return this.names; }
}

/**
 * The arc of a dedication: `dedication_gap_px` further out than the name,
 * on the uniform arc of R558, so the line keeps its gap over the name. The
 * named trench (R1251) and the seamount name (R2633) both take it.
 */
function dedicationArc(
  ctx: ChartContext, loop: readonly Point[], offset: number, text: string,
): { text: string; pts: Pt[]; startS: number } {
  const style = ctx.cfg.pieces.place_names[ctx.reading];
  const dm = ctx.glyphs.stepMetrics(faceOf(ctx.reading), T.text.dedication);
  const dlen = runWidth(text, dm, style.fig.dedication_spacing);
  const outer = offsetLoop(loop, offset + style.fig.dedication_gap_px);
  const darc = uniformArc(outer, dlen, T.fig.curve_deg, T.fig.arc_step_px);
  return { text, pts: darc, startS: polylineLength(darc, false) / 2 };
}

/** The middle of a sampled arc: its middle point, or the mean of its middle two. */
function arcMiddle(pts: readonly Pt[]): Pt {
  const a = pts[(pts.length - 1) >> 1], b = pts[pts.length >> 1];
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * One arc of uniform curvature, `len` px long, that turns through
 * `curveDeg` in all and passes through the top-most point of `rim` at its
 * middle (R558).
 *
 * The circle's centre lies straight below that apex, at the radius the
 * length and the turn give, so the name is level and bulges upward over
 * the crown. The points run left to right, sampled every `stepPx`, and the
 * run is centred on the arc's middle. A turn of 0 is a straight, level
 * line through the apex.
 *
 * The memorial reef of card 63 lays its title the same way (R964), so the
 * arc is exported rather than restated there: one name over a body of
 * water bulges one way in this chart.
 */
export function uniformArc(
  rim: readonly (readonly [number, number])[],
  len: number, curveDeg: number, stepPx: number,
): Pt[] {
  const crown = crownOf(rim);
  const apex = { x: rim[crown][0], y: rim[crown][1] };
  // Up, and left to right along the arc.
  const dx = 0, dy = -1;
  const px = 1, py = 0;
  const theta = (Math.max(0, curveDeg) * Math.PI) / 180;
  const n = Math.max(2, Math.ceil(len / Math.max(1, stepPx)) + 1);
  const out: Pt[] = [];
  if (theta < 1e-6) {
    for (let i = 0; i < n; i++) {
      const t = (i / (n - 1) - 0.5) * len;
      out.push({ x: apex.x + px * t, y: apex.y + py * t });
    }
    return out;
  }
  const r = len / theta;
  const cx = apex.x - dx * r, cy = apex.y - dy * r;
  for (let i = 0; i < n; i++) {
    const a = (i / (n - 1) - 0.5) * theta;
    const ux = dx * Math.cos(a) + px * Math.sin(a);
    const uy = dy * Math.cos(a) + py * Math.sin(a);
    out.push({ x: cx + ux * r, y: cy + uy * r });
  }
  return out;
}

/** The one instance the registry holds. */
export const placeNames = new PlaceNamesPiece();
